/**
 * roomService.ts
 * Firebase Realtime Database operations for room lifecycle management.
 *
 * Room data lives in RTDB (not Firestore) for minimum latency:
 *   /rooms/{roomCode}/...
 *
 * Firestore is used only for durable, queryable class/student data.
 */

import {
  ref,
  set,
  get,
  update,
  onValue,
  off,
  serverTimestamp,
  type Unsubscribe,
  type DatabaseReference,
} from 'firebase/database';
import { getRtdb, getCurrentUid } from './firebase';
import type { Room, RoomStatus } from '@types-local/room.types';
import type { ActivityConfig } from '@types-local/quiz.types';
import { generateRoomCode } from '@utils/codeGenerator';

// ---- Path helpers ------------------------------------------

const roomRef = (roomCode: string): DatabaseReference =>
  ref(getRtdb(), `rooms/${roomCode}`);

// ---- Room lifecycle ----------------------------------------

/**
 * Creates a new room in RTDB and returns the generated room code.
 */
export async function createRoom(
  classId: string,
  presentationTitle: string,
): Promise<string> {
  const uid = getCurrentUid();
  if (!uid) throw new Error('Not authenticated');

  const roomCode = generateRoomCode();

  const room: Omit<Room, 'createdAt'> & { createdAt: object } = {
    roomCode,
    classId,
    teacherId: uid,
    activeSlideId: null,
    activeConfig: null,
    status: 'waiting',
    presentationTitle,
    createdAt: serverTimestamp(),
  };

  await set(roomRef(roomCode), room);
  return roomCode;
}

/**
 * Fetches a room snapshot once.
 */
export async function getRoom(roomCode: string): Promise<Room | null> {
  const snapshot = await get(roomRef(roomCode));
  if (!snapshot.exists()) return null;
  return snapshot.val() as Room;
}

/**
 * Updates the active slide and config for a room.
 * Called as the teacher navigates through slides during a presentation.
 */
export async function updateActiveSlide(
  roomCode: string,
  slideId: string,
  config: ActivityConfig | null,
): Promise<void> {
  await update(roomRef(roomCode), {
    activeSlideId: slideId,
    activeConfig: config,
  });
}

/**
 * Updates the room status (waiting → active → results → closed).
 */
export async function updateRoomStatus(
  roomCode: string,
  status: RoomStatus,
): Promise<void> {
  await update(roomRef(roomCode), { status });
}

/**
 * Marks the room as closed and clears sensitive config from RTDB.
 */
export async function closeRoom(roomCode: string): Promise<void> {
  await update(roomRef(roomCode), {
    status: 'closed',
    activeConfig: null,
  });
}

// ---- Real-time subscription --------------------------------

/**
 * Subscribes to real-time room updates.
 * Returns an unsubscribe function — call in useEffect cleanup.
 */
export function subscribeToRoom(
  roomCode: string,
  callback: (room: Room | null) => void,
): Unsubscribe {
  const r = roomRef(roomCode);
  const listener = onValue(r, (snapshot) => {
    callback(snapshot.exists() ? (snapshot.val() as Room) : null);
  });

  // Return cleanup function
  return () => off(r, 'value', listener);
}
